// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

export enum DataType {
  FLOAT,
  DOUBLE,
  INT,
  BYTE,
}

export function extractArrayData(datastream: string, dataType: DataType, nrArrayElements?: number) {
  const byteCount =
    dataType === DataType.FLOAT
      ? 4
      : dataType === DataType.DOUBLE
      ? 8
      : dataType === DataType.INT
      ? 4
      : dataType === DataType.BYTE
      ? 1
      : 0;
  const nrValues = nrArrayElements ?? Math.ceil(datastream.length / (2 * byteCount));
  const desiredStringLength = nrValues * byteCount * 2;
  /* do not issue the following warning, since this may happen when a longer parameter set
     gets replaced by a shorter one in the LINX...
  if (desiredStringLength < datastream.length) {
    console.warn("longer array string than expected", datastream, dataType, nrArrayElements);
  }
  */
  const paddedString = datastream.padEnd(desiredStringLength, "0");
  const values = [];
  // loop through all data
  for (let i = 0; i < nrValues; i++) {
    // extract value depending on data type
    let val = 0;
    if (dataType === DataType.FLOAT) val = extractFloat(paddedString, i);
    else if (dataType === DataType.DOUBLE) val = extractDouble(paddedString, i);
    else if (dataType === DataType.INT) val = extractInt(paddedString, 4, i);
    else if (dataType === DataType.BYTE) val = extractInt(paddedString, 1, i);

    // append to array
    values[i] = val;
  }

  return values;
}

export function extractInt(
  valueString: string,
  size: number,
  position?: number,
  doPadding?: boolean,
) {
  const pos = position ?? 0;
  const valueStringPadded = doPadding ? valueString.padEnd(size * 2 * (pos + 1), "0") : valueString;
  // check for existing entry in string
  if (valueStringPadded.length < size * 2 * (pos + 1)) {
    console.trace("extract int string too short", valueString, size, pos, doPadding);
    return 0;
  }
  // extract value at given position
  const value = byteSwap(valueStringPadded.substring(size * 2 * pos, size * 2 * (pos + 1)));
  // convert to integer
  return parseInt(value, 16);
}

export function extractFloat(valueString: string, position?: number, doPadding?: boolean) {
  const size = 4;
  const pos = position ?? 0;
  if (typeof valueString !== "string") valueString = ""; // should not happen...
  const valueStringPadded = doPadding ? valueString.padEnd(size * 2 * (pos + 1), "0") : valueString;
  // check for existing entry in string
  if (valueStringPadded.length < size * 2 * (pos + 1)) {
    console.trace("extract float string too short", valueString, pos, doPadding);
    return 0;
  }

  // extract value at given position
  const swappedString = byteSwap(valueStringPadded.substring(size * 2 * pos, size * 2 * (pos + 1)));

  // check for zero string
  if (swappedString === "00000000") return 0;

  // convert to float
  return toFloat32(swappedString);
}

export function extractDouble(valueString: string, position?: number, doPadding?: boolean) {
  // extract value at given position
  const size = 8;
  const pos = position ?? 0;
  if (typeof valueString !== "string") valueString = ""; // should not happen...
  const valueStringPadded = doPadding ? valueString.padEnd(size * 2 * (pos + 1), "0") : valueString;

  const swappedValueString = byteSwap(
    valueStringPadded.substring(size * 2 * pos, size * 2 * (pos + 1)),
  );

  // convert to float
  return toFloat64(swappedValueString);
}

function hexByteToBin(byteString: string) {
  // get numeric value of byte
  const value = parseInt(byteString, 16);

  // convert number to binary string
  let binString = value.toString(2);

  // add leading 0s
  while (binString.length < 8) {
    binString = "0" + binString;
  }

  return binString;
}

export function byteSwap(byteString: string) {
  if (byteString.length % 2) {
    console.trace(`illegal string length: ${byteString} ${byteString.length}`);
  }

  let swapped = "";
  for (let i = 0; i < byteString.length / 2; i++) {
    swapped += byteString.substring(byteString.length - 2 * (i + 1), byteString.length - 2 * i);
  }

  return swapped;
}

function toFloat32(byteString: string) {
  // we cannot use Buffer.readFloatLE() here, as Buffer is not present in browsers
  const buffer = new ArrayBuffer(4);
  const view = new DataView(buffer);
  for (let i = 0; i < 4; i++) {
    view.setUint8(i, parseInt(byteString.substring(i * 2, i * 2 + 2), 16));
  }
  const result = view.getFloat32(0, false);
  return result;
}

function toFloat64(byteString: string) {
  const buffer = new ArrayBuffer(8);
  const view = new DataView(buffer);
  for (let i = 0; i < 8; i++) {
    view.setUint8(i, parseInt(byteString.substring(i * 2, i * 2 + 2), 16));
  }
  const result = view.getFloat64(0, false);

  /* manual implementation misses handling for NaNs
  // split string into 3 parts and convert each part to a number
  const noSign = parseInt(noBin.substring(0, 1), 2);
  const noExp = parseInt(noBin.substring(1, 12), 2);
  const noMant = parseInt(noBin.substring(12, 64), 2); // !!!!!!!!looses precision here!!!!!!!!
  const result = Math.pow(-1, noSign) * (1 + noMant / Math.pow(2, 52)) * Math.pow(2, noExp - 1023);
  */
  return result;
}

export function anyToFloat(
  input: number | boolean | null | undefined,
  setUndefinedToZero?: boolean,
) {
  if (input === null || input === undefined) {
    return setUndefinedToZero ? 0 : NaN;
  }
  if (typeof input === "boolean") {
    return input ? 1 : 0;
  }
  return input;
}

export function float32ToStr(input: number | boolean | undefined) {
  const buf = Buffer.allocUnsafe(4);
  const number = typeof input === "boolean" ? (input ? 1 : 0) : input;
  // little endian
  buf.writeFloatLE(number ?? NaN);
  return buf.toString("hex");
}

export function double64ToStr(input: number | undefined) {
  const buf = Buffer.allocUnsafe(8);
  // little endian
  buf.writeDoubleLE(input ?? NaN);
  return buf.toString("hex");
}

export function intToStr(size: number, inNumber: number | bigint | undefined) {
  const buf = Buffer.allocUnsafe(size);
  // NaN is a valid input so we need to check for this
  // otherwise BigInt(NaN) will throw an exception
  if (
    (typeof inNumber === "number" && isNaN(inNumber)) ||
    inNumber === undefined ||
    inNumber === null
  ) {
    inNumber = -1; // we use -1 as NaN for integers
  }

  // little endian
  try {
    if (size === 1) buf.writeInt8(inNumber as number);
    else if (size === 2) buf.writeInt16LE(inNumber as number);
    else if (size === 4) buf.writeInt32LE(inNumber as number);
    else if (size === 8) buf.writeBigInt64LE(BigInt(inNumber));
    else {
      console.trace("illegal intToStr size", size);
      buf.fill(0);
    }
  } catch (exc: unknown) {
    console.warn("range error in intToStr", (exc as RangeError).message);
    // ensure defined value for buffer, even if it is wrong
    buf.fill(0);
  }
  return buf.toString("hex");
}

export function uIntToStr(size: number, inNumber: number | bigint | undefined) {
  if (inNumber === undefined || inNumber === null) {
    // we cannot represent null/undefined in unsigned integers, so we need to choose zero
    inNumber = 0;
  }
  const buf = Buffer.allocUnsafe(size);
  // little endian
  if (size === 1) buf.writeUInt8(inNumber as number);
  else if (size === 2) buf.writeUInt16LE(inNumber as number);
  else if (size === 4) buf.writeUInt32LE(inNumber as number);
  else if (size === 8) buf.writeBigUInt64LE(BigInt(inNumber));
  else console.trace("illegal uIntToStr size", size);
  return buf.toString("hex");
}

export function boolToStr(inNumber: number | boolean | undefined) {
  if (inNumber) {
    return "01";
  }
  return "00";
}

export function itemArrayToStr<T>(inData: T[], convFunct: (inData: T) => string) {
  let result = "";
  // do not use forEach here as we may face a sparce inData array
  for (let i = 0; i < inData.length; i++) {
    result += convFunct(inData[i]);
  }
  return result;
}

export function get_ajax_error(error: any): string {
  if (["AbortError", "TimeoutError"].includes((error as Error).name)) {
    return "Communication Timeout";
  }
  return (error.response?.statusText ?? error.message ?? error.statusText ?? error) as string;
}

export function isBase64(input: string | undefined | any) {
  if (!input || typeof input !== "string") return false;
  if (input.length % 4 !== 0) return false;
  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(input)) return false;
  return true;
}

export function crc16(data: Buffer) {
  let crc = 0xffff;

  for (let i = 0; i < data.length; ++i) {
    crc ^= data[i] << 8;
    for (let j = 0; j < 8; ++j) {
      crc = (crc & 0x8000) > 0 ? (crc << 1) ^ 0x1021 : crc << 1;
    }
  }
  return crc & 0xffff;
}

export function stripTrailingZeros(data: string | string[] | undefined): string {
  if (data === undefined) return "";
  const dataString = Array.isArray(data) ? data[0] : data;
  if (typeof dataString !== "string") return "";
  return dataString.replace(/0+$/, "");
}

export function parseApiString(dataString: string | null | undefined): string {
  if (dataString) {
    return dataString;
  }
  // provide default value
  return "";
}

export function parseApiBool(dataString: string | number | boolean | null | undefined): boolean {
  if (dataString) {
    if (typeof dataString === "string") {
      if (dataString === "true") {
        return true;
      } else if (dataString === "false") {
        return false;
      } else {
        return parseApiBool(parseInt(dataString, 10));
      }
    } else if (typeof dataString === "number") {
      return dataString !== 0;
    }
    return dataString;
  }
  // provide default value
  return false;
}

export function parseApiInt(
  dataString: string | number | null | undefined,
  defaultValue?: number,
): number {
  if (dataString !== undefined && dataString !== null) {
    if (typeof dataString === "string") {
      const numVal = parseInt(dataString, 10);
      if (!isNaN(numVal)) {
        return numVal;
      } else {
        return defaultValue ?? 0;
      }
    }
    return dataString;
  }
  // provide default value
  return defaultValue ?? 0;
}

export function parseApiFloat(dataString: string | number | undefined | null): number {
  if (dataString !== undefined && dataString !== null) {
    if (typeof dataString === "string") {
      return parseFloat(dataString);
    }
    return dataString;
  }
  // provide default value
  return NaN;
}

export function parseApiDate(dateString: string | Date | undefined): Date | undefined {
  if (dateString) {
    if (typeof dateString === "string") {
      const unixEpoch = Date.parse(dateString);
      if (!isNaN(unixEpoch)) {
        return new Date(unixEpoch);
      }
    } else {
      if (typeof dateString === "object") {
        return dateString;
      }
    }
  }
  // provide illegal value
  return undefined;
}

export function parseApiItemArray<ItemTypeIn, ItemTypeOut extends ItemTypeIn>(
  dataString: (ItemTypeIn | undefined | null)[] | null | undefined,
  itemParser: (item: ItemTypeIn | undefined | null) => ItemTypeOut = (item) => item as ItemTypeOut,
  defaultValue: ItemTypeOut[] = [],
): ItemTypeOut[] {
  if (Array.isArray(dataString)) {
    const newArray: ItemTypeOut[] = [];
    dataString.forEach((curValue) => {
      newArray.push(itemParser(curValue));
    });
    return newArray;
  }
  return defaultValue;
}
